import { S3_URL, USER_TYPES } from 'constants/index';
import React, { useEffect, useState } from 'react';
import { Image, Nav, NavDropdown, Dropdown, Button } from 'react-bootstrap';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { IMAGE_PATHS } from 'constants/imagePaths';
import {
	logLogout,
	logoutZIM,
	useGetChatUsersQuery,
	useGetLanguageListQuery,
	useLogoutMutation,
	useNotificationListQuery,
	useNotificationReadAllMutation,
	useNotificationReadMutation,
} from 'services';
import useAuthStore, { resetState, setUserInfo } from 'store/auth';
import useLocalizationStore from 'store/localization';
import {
	clearStorageExceptPrefix,
	dateFormatForNotifications,
	getCreatedDifferenceInBestUnit,
	getNotificationURL,
	languageCode,
} from 'utils';
import SearchSuggestions from 'components/SearchSuggestions';
import { disconnect } from 'store/socket';
import useChatStore, { setNewMessageAlert, setUserList } from 'store/chat';
import { isMobileOnly, isTablet, useMobileOrientation } from 'react-device-detect';
import useTranslate from 'hooks/useTranslate';
import { changeLanguage } from 'services/base.service';
import { resetFeeds } from 'store/feeds';
import { resetAllStores } from 'store/resetStore';
import useUserStore, { setNewNotificationAlert } from 'store/user';
import Loader from '../Loader';
import { secureStorage } from 'utils/secureStorage';

const PrivateHeader: React.FC = () => {
	const { translate } = useTranslate();
	const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);

	const { token: isAuthenticate, user }: Record<string, any> = useAuthStore(
		(state) => state.userInfo ?? {},
	);
	const { activeLanguage, setActiveLanguage }: any = useLocalizationStore((state) => state);
	const userDetails: any = useAuthStore((state) => state.userInfo.user);
	const newMessageAlert = useChatStore((state) => state.newMessageAlert);
	const { newNotificationAlert } = useUserStore((state) => state);
	const { isLandscape } = useMobileOrientation();
	const navigate = useNavigate();
	const payload = `limit=15`;
	const {
		data: notifications = {},
		refetch,
		isPending: isPendingNotification,
	} = useNotificationListQuery(payload);

	// mutation
	const { mutateAsync: logoutMutation, isPending } = useLogoutMutation();
	const { mutateAsync: notificationReadAMutation } = useNotificationReadMutation();
	const { data: { language = [] } = {} } = useGetLanguageListQuery();

	const handleNotificationToggle = (isOpen: boolean) => {
		if (isOpen) {
			// notificationReadAllMutation().then((res: any) => {
			// 	if (res?.success) setNewNotificationAlert(false);
			// });
		}
		setShowNotificationDropdown(isOpen);
	};

	useEffect(() => {
		setNewNotificationAlert(notifications?.meta?.unreadCount);
	}, [notifications?.meta?.unreadCount]);

	useEffect(() => {
		if (showNotificationDropdown) {
			refetch();
		}
	}, [showNotificationDropdown]);

	const handleLogout = () => {
		logoutMutation()
			.then((res: any) => {
				resetFeeds();
				clearStorageExceptPrefix(sessionStorage, ['uc_']);
				clearStorageExceptPrefix(localStorage, ['uc_', 'i18nextLng']);
				resetAllStores();
				if (res?.statusCode === 401) {
					secureStorage.removeItem('fcmToken');
					logoutZIM();
					disconnect();
					resetState();
				}

				if (res?.success) {
					secureStorage.removeItem('fcmToken');
					logoutZIM();
					disconnect();
					setUserInfo({});
				}
			})
			.finally(() => {
				logLogout();
			});
	};

	const selectedLang = language.find((data: any) => data.slug === activeLanguage);
	const { data: chatUsersList = [], isPending: isPendingChatUser } = useGetChatUsersQuery('');
	const recentMessages = useChatStore((state) => state.userList);
	useEffect(() => {
		if (isPendingChatUser) return;

		// Only set the user list when we actually have data and it differs from the store
		if (!Array.isArray(chatUsersList) || chatUsersList.length === 0) return;

		const existing = Array.isArray(recentMessages) ? recentMessages : [];
		// Quick deep-equality check to avoid setting state unnecessarily
		const isSame =
			existing.length === chatUsersList.length &&
			JSON.stringify(existing) === JSON.stringify(chatUsersList);

		if (!isSame) {
			setUserList(chatUsersList);
		}
	}, [chatUsersList, isPendingChatUser, recentMessages]);

	const onUserSelect = (room_id: number, receiver_id: number) => {
		if (room_id && receiver_id) {
			navigate(`/chat/${receiver_id}/${room_id}`);
		}
	};

	const handleClickOnNotification = async (
		type: string,
		id: any,
		redirectOn: string | null = null,
		data: any,
		notificationId: any,
		isRead: boolean,
	) => {
		try {
			if (!isRead) {
				await notificationReadAMutation({ id: notificationId });
				refetch();
			}
			if (type === 'profileView') {
				return navigate(`/${data?.userType}/profile/${data?.id}`);
			}
			const url: any = await getNotificationURL(type, id, null, userDetails?.userType, redirectOn);
			navigate(url);
		} finally {
			setShowNotificationDropdown(false);
		}
	};

	const goToProfile = (data: any) => {
		navigate(`/${data?.userType}/profile/${data?.id}`);
		setShowNotificationDropdown(false);
	};

	return (
		<>
			<Nav id="responsive-navbar-nav" className="justify-content-end flex-1 bg-white">
				<Nav className="mx-auto align-items-center center-nav">
					<Nav.Link as={NavLink} to="/" className="position-relative p-0">
						{translate('Header.home')}
					</Nav.Link>
					{isAuthenticate && user?.userType === 'company' && (
						<>
							<Nav.Link as={NavLink} to="/company/post-new-job" className="position-relative p-0">
								{translate('Header.postJob')}
							</Nav.Link>
							<Nav.Link as={NavLink} to="/company/my-jobs" className="position-relative p-0">
								{translate('Header.jobs')}
							</Nav.Link>
						</>
					)}
					<Nav.Link as={NavLink} to="/social-feed-posts" className="position-relative p-0">
						{translate('Header.feeds')}
					</Nav.Link>

					{/* {user?.userType === USER_TYPES?.SEEKER && (
						<Nav.Link as={NavLink} to="/job-search" className="position-relative p-0">
							{translate('Sidebar.jobs')}
						</Nav.Link>
					)} */}

					{user?.userType === USER_TYPES?.COMPANY && (
						<Nav.Link as={NavLink} to="/company/subscriptions" className="position-relative p-0">
							{translate('Header.plans')}
						</Nav.Link>
					)}
				</Nav>
				<Nav className="align-items-center right-nav pt-0">
					<div className="d-none d-lg-block">
						<SearchSuggestions />
					</div>
					{selectedLang && (
						<Dropdown className="language-selector">
							<Dropdown.Toggle id="dropdown-basic" className="bg-transparent p-0 border-0 w-auto">
								<Image
									src={`${S3_URL}${selectedLang?.image}`}
									alt="flag"
									className="h-auto me-1"
									loading="lazy"
								/>{' '}
								<span>{selectedLang?.title}</span>
							</Dropdown.Toggle>

							<Dropdown.Menu>
								{language?.length > 0 &&
									language.map((lang: any) => (
										<Dropdown.Item
											className={`${languageCode[lang?.title] === selectedLang?.code ? 'active' : ''} `}
											key={lang.code}
											onClick={() => {
												changeLanguage(lang?.slug);
												setActiveLanguage(lang?.slug);
											}}
										>
											<Image
												src={`${S3_URL}${lang?.image}`}
												alt="flag"
												className="h-auto me-1"
												loading="lazy"
											/>
											{lang?.title}
										</Dropdown.Item>
									))}
							</Dropdown.Menu>
						</Dropdown>
					)}

					<Dropdown
						show={showNotificationDropdown}
						className="chat-notifications"
						onToggle={handleNotificationToggle}
					>
						<Dropdown.Toggle
							id="dropdown-basic"
							className="white-btn bg-white rounded-circle p-0 border-0 w-auto"
						>
							<i className="ri-notification-4-line"></i>
							{newNotificationAlert > 0 && (
								<div className="chat-notify">{newNotificationAlert}</div>
							)}
						</Dropdown.Toggle>

						<Dropdown.Menu className="nav-search-options position-absolute mt-2 bg-white w-100">
							<div className="nav-search-options-result">
								{isPendingNotification ? <Loader /> : ''}
								{!isPendingNotification && notifications?.docs?.length > 0 ? (
									notifications?.docs?.map((notification: any, index: any) => {
										const { userType } = notification?.triggeredByUser || {};
										return (
											index < 5 && (
												<a
													key={index}
													className="searched-user notification-drop d-flex align-items-center gap-2 gap-sm-3"
													style={notification?.isRead ? {} : { backgroundColor: '#e9e9e9' }}
												>
													<div className="searched-user-img">
														<Image
															onClick={() => goToProfile(notification?.triggeredByUser)}
															style={{ cursor: 'pointer' }}
															src={`${S3_URL}${notification?.triggeredByUser?.[userType]?.profilePicture}`}
															onError={({ currentTarget }) => {
																currentTarget.onerror = null;
																currentTarget.src = IMAGE_PATHS.UserDefault;
															}}
															className="rounded-circle h-100 w-100 object-fit-cover"
															loading="lazy"
														/>
													</div>
													<div
														onClick={() =>
															handleClickOnNotification(
																notification?.notifyType,
																notification?.notificationTypeId,
																userType,
																notification?.triggeredByUser,
																notification?.id,
																notification?.isRead,
															)
														}
														className="searched-user-detail flex-1"
													>
														<div className="d-flex flex-column flex-sm-row align-items-sm-center gap-sm-2">
															<h6 className="mb-0 fw-bold flex-1">
																{
																	notification?.triggeredByUser?.[userType][
																		userType === USER_TYPES.COMPANY ? 'companyName' : 'userName'
																	]
																}
															</h6>
															<p className="chat-notify-time mb-0">
																{dateFormatForNotifications(notification?.createdAt)}
															</p>
														</div>
														<p className="chat-msg mb-0">{notification?.message}</p>
													</div>
												</a>
											)
										);
									})
								) : (
									<p className="px-4 py-2 mb-0">{translate('Common.noNotificationFound')}</p>
								)}
							</div>
							{notifications?.docs?.length > 0 && (
								<div className="text-center p-2 p-md-3">
									<Button
										variant=""
										onClick={() => {
											navigate('/notifications');
											setShowNotificationDropdown(false);
										}}
										className="view-searched-users fw-bold border-0 p-0"
									>
										{translate('Common.viewAllNotification')}
									</Button>
								</div>
							)}
						</Dropdown.Menu>
					</Dropdown>

					<Dropdown
						className="chat-notifications"
						onToggle={(show: boolean) => {
							if (show) setNewMessageAlert(false);
						}}
					>
						<Dropdown.Toggle
							id="dropdown-basic"
							className="white-btn bg-white rounded-circle p-0 border-0 w-auto chat"
						>
							<i className="ri-chat-3-line"></i>
							{newMessageAlert && <div className="chat-notify"></div>}
						</Dropdown.Toggle>

						<Dropdown.Menu className="nav-search-options position-absolute mt-2 bg-white w-100">
							<div className="nav-search-options-result">
								{recentMessages?.length > 0 ? (
									recentMessages.slice(0, 5)?.map((user: any, index: number) => (
										<a
											key={index}
											className="searched-user d-flex align-items-center gap-3"
											onClick={() => onUserSelect(user?.roomId, user?.chatUserId)}
										>
											<div className="searched-user-img">
												<Image
													src={
														user?.profilePicture
															? S3_URL + user.profilePicture
															: IMAGE_PATHS.UserDefault
													}
													className="rounded-circle h-100 w-100 object-fit-cover"
													loading="lazy"
												/>
											</div>
											<div className="searched-user-detail flex-1">
												<div className="d-flex align-items-center gap-2">
													<div className="flex-1 d-flex align-items-center gap-2">
														<h6 className="mb-0 fw-bold">{user?.name}</h6>
														{user?.unreadCount > 0 && <div className="chat-notify-user"></div>}
													</div>
													<p className="chat-notify-time mb-0">
														{getCreatedDifferenceInBestUnit(user?.time)}
													</p>
												</div>
												{user?.messageType !== 'text' ? (
													<div className="file-detail d-flex align-items-center gap-1">
														{user?.messageType === 'audio' && (
															<>
																<i className="ri-music-2-fill"></i>
																{translate('Header.audio')}
															</>
														)}
														{user?.messageType === 'video' && (
															<>
																<i className="ri-file-video-fill"></i> {translate('Header.video')}
															</>
														)}
														{user?.messageType === 'image' && (
															<>
																<i className="ri-image-2-fill"></i> {translate('Header.image')}
															</>
														)}
														{user?.messageType === 'doc' && (
															<>
																<i className="ri-file-fill"></i> {translate('Header.doc')}
															</>
														)}
													</div>
												) : (
													<p className="chat-msg mb-0">{user?.message}</p>
												)}
											</div>
										</a>
									))
								) : (
									<p className="px-4 py-2 mb-0">{translate('Header.noMessages')}</p>
								)}
							</div>
							{recentMessages?.length > 0 && (
								<div className="text-center p-2 p-md-3">
									<Button
										variant=""
										onClick={() =>
											isMobileOnly || (isTablet && !isLandscape)
												? navigate('/chat-list')
												: onUserSelect(recentMessages[0]?.roomId, recentMessages[0]?.chatUserId)
										}
										className="view-searched-users fw-bold border-0 p-0"
									>
										{translate('Header.viewAllMessages')}
									</Button>
								</div>
							)}
						</Dropdown.Menu>
					</Dropdown>

					<NavDropdown
						title={
							<div className="d-flex align-items-center">
								<Image
									src={
										userDetails?.profilePicture
											? `${S3_URL}${userDetails.profilePicture}`
											: IMAGE_PATHS.UserDefault
									}
									className="rounded-circle"
									loading="lazy"
								/>
								<div className="circle-arrow rounded-circle ms-2">
									<i className="ri-arrow-down-s-line"></i>
								</div>
							</div>
						}
						id="basic-nav-dropdown"
						className="profile-dropdown d-none d-xl-block"
					>
						<NavDropdown.Item
							as={Link}
							to={user?.userType === USER_TYPES.COMPANY ? '/company/profile' : '/seeker/profile'}
						>
							{translate('Header.viewProfile')}
						</NavDropdown.Item>

						<NavDropdown.Divider />
						<NavDropdown.Item onClick={() => handleLogout()} disabled={isPending}>
							{translate('Header.logout')}
						</NavDropdown.Item>
					</NavDropdown>
				</Nav>
			</Nav>
			<div className="d-lg-none">
				<SearchSuggestions />
			</div>
		</>
	);
};

export default PrivateHeader;
